import { useEffect, useRef, useState } from "react";
import "./FormComponents.css";
import { timeSlots } from "./OptionsList";
// import TimeZoneDropdown from "./TimeZoneDropdown";

export default function PickTime({
  setTimeSelected,
  setTimeZoneSelected,
  setDurationSelected,
  selectedDate,
  selectedTimeZone
}) {
  const [timeSlot, setTimeSlot] = useState(null);
  const [duration, setDuration] = useState(0);
  const slots = timeSlots[duration];
  const timeSlotsRef = useRef(null);

  const handleClick = (e, index) => {
    // Get the original (base) time slot from the slots array
    const originalSlot = slots[index - 1];

    // Check if the slot is disabled based on the original slot time
    if (isWeekend() || (isSelectedDateToday() && isSlotDisabled(originalSlot))) {
      return;
    }

    // Get the converted time that's displayed to the user
    const convertedSlot = convertTimeZone(originalSlot, selectedTimeZone, "GMT+5:30");
    setTimeSlot(index);

    // Set the converted time slot for display, but store original time reference internally
    setTimeSelected(convertedSlot);
  };

  const parseTime = (timeStr) => {
    const [startTime] = timeStr.split(" - ");
    const [hour, minute, period] = startTime.split(/[:\s]/);
    let hours = parseInt(hour, 10);
    if (period === "PM" && hours !== 12) hours += 12;
    if (period === "AM" && hours === 12) hours = 0;
    return new Date().setHours(hours, parseInt(minute, 10), 0, 0);
  };

  const isSlotDisabled = (slotTime) => {
    const now = new Date();
    if (isWeekend()) return true;
    if (isSelectedDateToday()) {
      return parseTime(slotTime) < now.getTime();
    }
    return false;
  };

  const isSelectedDateToday = () => {
    const today = new Date();
    return (
      selectedDate &&
      today.getFullYear() === selectedDate.getFullYear() &&
      today.getMonth() === selectedDate.getMonth() &&
      today.getDate() === selectedDate.getDate()
    );
  };

  const isWeekend = () => {
    const date = selectedDate;
    if (!date) return false;
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  const hasAvailableSlots = () => {
    if (isWeekend()) {
      return false;
    }
    return slots.some((slot) => !isSlotDisabled(slot));
  };

  useEffect(() => {
    const hasAvailableSlot = hasAvailableSlots();

    if (hasAvailableSlot) {
      let slotIndex = -1;
      const firstAvailableIndex = slots.findIndex((slot) => !isSlotDisabled(slot));
      if (isSelectedDateToday()) {
        slotIndex = firstAvailableIndex;
      } else {
        slotIndex = 0;
      }
      if (timeSlot === null && slotIndex !== -1) {
        if (isWeekend()) return;
        // Convert the time slot before setting it
        const convertedSlot = convertTimeZone(slots[slotIndex], selectedTimeZone, "GMT+5:30");
        setTimeSelected(convertedSlot);
        setTimeSlot(slotIndex + 1);
      } else if (timeSlot !== null) {
        // Convert the time slot before setting it
        const convertedSlot = convertTimeZone(slots[slotIndex], selectedTimeZone, "GMT+5:30");
        setTimeSelected(convertedSlot);
        setTimeSlot(slotIndex + 1);
      } else {
        if (timeSlot && timeSlot > 0) {
          const slotToConvert = slots[timeSlot - 1] || "No available time slots for the date selected";
          const convertedSlot = typeof slotToConvert === 'string' && slotToConvert.includes('-')
            ? convertTimeZone(slotToConvert, selectedTimeZone, "GMT+5:30")
            : slotToConvert;
          setTimeSelected(convertedSlot);
        } else {
          setTimeSelected("No available time slots for the date selected");
        }
      }
    } else {
      setTimeSelected("No available time slots for the date selected");
    }
    // eslint-disable-next-line
  }, [duration, selectedDate, selectedTimeZone]);

  useEffect(() => {
    if (timeSlotsRef.current) {
      const firstAvailableIndex = slots.findIndex((slot) => !isSlotDisabled(slot));
      if (firstAvailableIndex !== -1) {
        const firstAvailableElement = timeSlotsRef.current.children[firstAvailableIndex];
        if (firstAvailableElement) {
          firstAvailableElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
          });
        }
      }
    }
    // eslint-disable-next-line
  }, [duration, selectedDate]);

  const handleDuration = (e, index) => {
    setDuration(index);
    setDurationSelected(e.target.innerText);
  };

  function convertTimeZone(timeString, selectedTimeZone, baseTimeZone = "GMT+5:30") {
    if (!timeString || !timeString.includes(" - ")) {
      return timeString;
    }

    const parseTimeZone = (tzString) => {
      const match = tzString.match(/GMT([+-])(\d+):(\d+)/i);
      if (!match) return { sign: 1, hours: 0, minutes: 0 };

      const sign = match[1] === '+' ? 1 : -1;
      const hours = parseInt(match[2], 10);
      const minutes = parseInt(match[3], 10);

      return { sign, hours, minutes };
    };

    const selected = parseTimeZone(selectedTimeZone);
    const base = parseTimeZone(baseTimeZone);
    const selectedMinutes = selected.sign * (selected.hours * 60 + selected.minutes);
    const baseMinutes = base.sign * (base.hours * 60 + base.minutes);
    const diffMinutes = selectedMinutes - baseMinutes;

    const hoursDiff = Math.floor(Math.abs(diffMinutes) / 60);
    const minutesDiff = Math.abs(diffMinutes) % 60;

    const hoursToAdd = diffMinutes < 0 ? -hoursDiff : hoursDiff;
    const minutesToAdd = diffMinutes < 0 ? -minutesDiff : minutesDiff;

    const [startTime, endTime] = timeString.split(' - ');

    function offsetTime(time) {
      const [timePart, meridiem] = time.split(' ');
      let [hours, minutes] = timePart.split(':').map(num => parseInt(num, 10));

      if (meridiem === 'PM' && hours !== 12) {
        hours += 12;
      } else if (meridiem === 'AM' && hours === 12) {
        hours = 0;
      }

      let totalMinutes = hours * 60 + minutes + hoursToAdd * 60 + minutesToAdd;

      while (totalMinutes < 0) {
        totalMinutes += 24 * 60;
      }
      totalMinutes %= 24 * 60;

      const newHours = Math.floor(totalMinutes / 60);
      const newMinutes = totalMinutes % 60;
      let displayHours = newHours % 12;
      if (displayHours === 0) displayHours = 12;
      const newMeridiem = newHours >= 12 ? 'PM' : 'AM';
      return `${displayHours}:${newMinutes.toString().padStart(2, '0')} ${newMeridiem}`;
    }

    const newStartTime = offsetTime(startTime);
    const newEndTime = offsetTime(endTime);
    return `${newStartTime} - ${newEndTime}`;
  }

  return (
    <div className="pick-time-wrapper">
      <div className="pick-time-header">
        <h2>Available Time Slots</h2>
      </div>
      <div className="time-duration-tab">
        <div
          onClick={(e) => handleDuration(e, 0)}
          className={`${duration === 0 ? "active" : ""}`}
        >
          <p>15min</p>
        </div>
        <div
          onClick={(e) => handleDuration(e, 1)}
          className={`${duration === 1 ? "active" : ""}`}
        >
          <p>30min</p>
        </div>
      </div>
      <div className="time-slots" ref={timeSlotsRef}>
        {slots.map((slot, index) => {
          // Check if slot is disabled based on the ORIGINAL slot time (in base timezone)
          const isDisabled = isSlotDisabled(slot) ? "time-slot-disabled" : "";
          // Convert the slot for display
          const convertedSlot = convertTimeZone(slot, selectedTimeZone, "GMT+5:30");

          return (
            <div
              key={index}
              onClick={(e) => handleClick(e, index + 1)}
              className={`time-slot ${timeSlot === index + 1 ? "time-slot-picked" : ""} ${isDisabled}`}
              style={{ cursor: isDisabled ? "not-allowed" : "pointer" }}
            >
              <p>{convertedSlot}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
